import { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import {
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
} from "@mui/icons-material";
import AddressbookPanelShell from "../../addressbook/panels/AddressbookPanelShell";
import ContactCard from "../../../components/component-contact/ContactCard";
import { apiGetContactsCollection } from "../../../graphQL/addressbook/collections/contacts";
import { apiGetCampaignContactLifecycles } from "../../../graphQL/campaign";
import { toContactCard } from "../../addressbook/presenters";
import { contactMatchesFilter } from "../../../utils/campaignContactFilter";

const PAGE_SIZE = 25;
// Lifecycle rows are keyed by contactId client-side; pull a generous window so
// the visible contact page is reliably covered.
const LIFECYCLE_FETCH_LIMIT = 2000;

// Real campaign lifecycle states emitted by the backend
// (campaignContactLifecycles.lifecycleState). Anything unknown falls back to a
// neutral style.
const LIFECYCLE_STATE_STYLE = {
  queued:    { color: "#92400E", bg: "#FEF3C7", border: "#F59E0B" },
  sending:   { color: "#1E40AF", bg: "#DBEAFE", border: "#3B82F6" },
  sent:      { color: "#065F46", bg: "#D1FAE5", border: "#10B981" },
  failed:    { color: "#991B1B", bg: "#FEE2E2", border: "#EF4444" },
  suspended: { color: "#475569", bg: "#E2E8F0", border: "#64748B" },
  rescinded: { color: "#4C1D95", bg: "#EDE9FE", border: "#7C3AED" },
};
const NEUTRAL_STATE_STYLE = { color: "#475569", bg: "#E2E8F0", border: "#64748B" };

// Post-send SES signals refine the basic lifecycle state: show the furthest
// signal reached. Bounce and suppression override engagement.
const SES_STATE_STYLE = {
  delivered:    { color: "#065F46", bg: "#D1FAE5", border: "#10B981" },
  opened:       { color: "#0C4A6E", bg: "#E0F2FE", border: "#0EA5E9" },
  clicked:      { color: "#312E81", bg: "#E0E7FF", border: "#6366F1" },
  bounced:      { color: "#991B1B", bg: "#FEE2E2", border: "#EF4444" },
  unsubscribed: { color: "#475569", bg: "#E2E8F0", border: "#64748B" },
};

const displayState = (lifecycle) => {
  const base = String(lifecycle?.lifecycleState || "");
  if (!lifecycle) return base;
  if (lifecycle.suppressed && lifecycle.suppressionReason === "unsubscribe-link") return "unsubscribed";
  if (lifecycle.bouncedAt || (lifecycle.suppressed && ["bounce", "complaint"].includes(lifecycle.suppressionReason))) return "bounced";
  if (base !== "sent") return base;
  if (lifecycle.clickedAt) return "clicked";
  if (lifecycle.openedAt) return "opened";
  if (lifecycle.deliveredAt) return "delivered";
  return base;
};

const contactKey = (contact) =>
  String(contact?._id || contact?.id || "");

const toList = (value) =>
  (Array.isArray(value) ? value : String(value || "").split(/[,\n;]+/))
    .map((v) => String(v || "").trim())
    .filter(Boolean);

const getFilter = (campaign) => {
  const data = campaign?.data && typeof campaign.data === "object" ? campaign.data : {};
  const filter = data?.contactFilter && typeof data.contactFilter === "object" ? data.contactFilter : {};
  // Campaign is scoped to its own domain — never accept `domain` from the
  // contact filter.
  const rawStatus = filter?.status;
  const statusArr = Array.isArray(rawStatus)
    ? rawStatus.map((v) => String(v || "").trim()).filter(Boolean)
    : (String(rawStatus || "").trim() ? [String(rawStatus).trim()] : []);
  return {
    domain: String(campaign?.domain || "").trim(),
    text: String(filter?.query || "").trim(),
    categories: toList(filter?.categories ?? filter?.category),
    status: statusArr,
    mode: String(filter?.mode || "AND").toUpperCase() === "OR" ? "OR" : "AND",
    cities: toList(filter?.cities),
    zipcodes: toList(filter?.zipcodes),
  };
};

const hasAnyFilter = (f) =>
  Boolean(f.text || f.categories.length || f.status.length || f.cities.length || f.zipcodes.length);

const CampaignContactsPanel = ({ campaign, authorId, lifecycleRefreshKey = 0, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [campaignStateFilter, setCampaignStateFilter] = useState("");
  // Real per-contact lifecycle rows, keyed by contactId. Empty when the
  // campaign has never been (dry-)sent — in that case we render the plain
  // filtered-contact preview with no badges.
  const [lifecycleByContact, setLifecycleByContact] = useState({});

  const filter = useMemo(() => getFilter(campaign), [campaign]);
  const saved = Boolean(campaign?.id) && !campaign?._isNew;
  const filtersActive = hasAnyFilter(filter);
  const categoriesKey = useMemo(() => filter.categories.join(","), [filter.categories]);
  const statusKey = useMemo(() => filter.status.join(","), [filter.status]);
  const citiesKey = useMemo(() => filter.cities.join(","), [filter.cities]);
  const zipcodesKey = useMemo(() => filter.zipcodes.join(","), [filter.zipcodes]);
  const needsLocalFiltering =
    filter.mode === "OR" || filter.cities.length > 0 || filter.zipcodes.length > 0;

  // Reset to page 1 whenever the campaign or its filter changes
  useEffect(() => { setPage(1); }, [
    campaign?.id,
    filter.domain,
    filter.text,
    categoriesKey,
    statusKey,
    filter.mode,
    citiesKey,
    zipcodesKey,
  ]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!campaign || !saved || !filter.domain) {
        setRows([]);
        setTotal(0);
        return;
      }
      setLoading(true);
      try {
        const filters = { domain: filter.domain };
        if (!needsLocalFiltering) {
          if (filter.text) filters.text = filter.text;
          if (filter.categories.length === 1) filters.category = filter.categories[0];
          if (filter.categories.length > 1) filters.categories = filter.categories;
          if (filter.status && filter.status.length === 1) filters.status = filter.status[0];
          if (filter.status && filter.status.length > 1) filters.statuses = filter.status;
        }

        const limit = needsLocalFiltering ? Math.max(PAGE_SIZE * page, 250) : PAGE_SIZE;
        const result = await apiGetContactsCollection({
          ...(authorId ? { authorId } : {}),
          filters,
          pagination: { page: needsLocalFiltering ? 1 : page, limit },
          ordering: { field: "updatedAt", order: "Desc" },
        });
        if (cancelled) return;
        const items = Array.isArray(result?.items) ? result.items : [];
        const filtered = items.filter((row) => contactMatchesFilter(row, filter));
        const visible = needsLocalFiltering
          ? filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
          : filtered;
        setRows(visible);
        setTotal(needsLocalFiltering ? filtered.length : Number(result?.total) || 0);
      } catch (err) {
        console.error("Failed to load campaign contacts:", err);
        if (!cancelled) {
          setRows([]);
          setTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    campaign?.id,
    saved,
    authorId,
    filtersActive,
    page,
    filter.domain,
    filter.text,
    categoriesKey,
    statusKey,
    filter.mode,
    citiesKey,
    zipcodesKey,
    needsLocalFiltering,
  ]);

  // Fetch the real campaign lifecycle rows for the selected campaign and key
  // them by contactId. Refetched whenever the campaign changes or a dry run
  // bumps lifecycleRefreshKey.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      const campaignId = campaign?.id;
      if (!campaignId || campaign?._isNew || !filter.domain) {
        setLifecycleByContact({});
        return;
      }
      try {
        const result = await apiGetCampaignContactLifecycles({
          campaignId,
          domain: filter.domain,
          pagination: { page: 1, limit: LIFECYCLE_FETCH_LIMIT },
        });
        if (cancelled) return;
        const items = Array.isArray(result?.items) ? result.items : [];
        const map = {};
        items.forEach((row) => {
          const key = String(row?.contactId || "");
          if (key) map[key] = row;
        });
        setLifecycleByContact(map);
      } catch (err) {
        console.error("Failed to load campaign contact lifecycles:", err);
        if (!cancelled) setLifecycleByContact({});
      }
    };
    run();
    return () => { cancelled = true; };
  }, [campaign?.id, campaign?._isNew, filter.domain, lifecycleRefreshKey]);

  // Reset the state filter to "all" whenever the campaign changes so a stale
  // filter never hides the new campaign's contacts.
  useEffect(() => { setCampaignStateFilter(""); }, [campaign?.id]);

  const hasLifecycle = Object.keys(lifecycleByContact).length > 0;
  // Drive the state-filter dropdown from the states actually present.
  const availableStates = useMemo(() => {
    const seen = new Set();
    Object.values(lifecycleByContact).forEach((row) => {
      const s = String(row?.lifecycleState || "").trim();
      if (s) seen.add(s);
    });
    return Array.from(seen).sort();
  }, [lifecycleByContact]);

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasPrev = page > 1;
  const hasNext = page < pageCount;
  const showingFrom = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, total);

  return (
    <AddressbookPanelShell
      onClose={onClose}
      collectionLabel="Campaign Contacts"
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", flex: 1, gap: 0.75, minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", whiteSpace: "nowrap" }}>
            Campaign Contacts
          </Typography>
          {campaign?.id && !campaign?._isNew && String(campaign?.status || "").toLowerCase() === "draft" ? (
            <Typography sx={{ fontSize: 11, color: "#92400E", fontStyle: "italic", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
              (campaign not activated yet)
            </Typography>
          ) : null}
          {filter.domain && hasLifecycle ? (
            <TextField
              select
              size="small"
              value={campaignStateFilter}
              onChange={(e) => setCampaignStateFilter(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              sx={{
                minWidth: 110,
                flexShrink: 0,
                "& .MuiOutlinedInput-root": { height: 26, fontSize: 12, borderRadius: 1, backgroundColor: "#fff" },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                <em>All states</em>
              </MenuItem>
              {availableStates.map((s) => (
                <MenuItem key={s} value={s} sx={{ fontSize: 12, textTransform: "capitalize" }}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          ) : null}
        </Box>
      }
      footerLeft={
        filter.domain ? (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            {total === 0 ? "0 matched" : `${showingFrom}–${showingTo} of ${total}`}
          </Typography>
        ) : (
          <Typography sx={{ color: "text.secondary", fontSize: 11 }}>
            Pick a campaign to load contacts
          </Typography>
        )
      }
      footerRight={
        filter.domain && total > PAGE_SIZE ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <Tooltip title="Previous page">
              <span>
                <IconButton
                  size="small"
                  disabled={!hasPrev || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ width: 24, height: 24 }}
                >
                  <PrevIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Typography sx={{ fontSize: 11, color: "#475569", mx: 0.5 }}>
              Page {page} / {pageCount}
            </Typography>
            <Tooltip title="Next page">
              <span>
                <IconButton
                  size="small"
                  disabled={!hasNext || loading}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                  sx={{ width: 24, height: 24 }}
                >
                  <NextIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : null
      }
    >
      {!filter.domain ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            Select a campaign with a domain to preview matching contacts.
          </Typography>
        </Box>
      ) : !saved ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 12, color: "#64748B" }}>
            Save the campaign to see its contacts.
          </Typography>
        </Box>
      ) : loading ? (
        <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {rows
            .map((contact) => ({
              contact,
              lifecycle: lifecycleByContact[contactKey(contact)] || null,
            }))
            // When a state filter is active, only show contacts whose real
            // lifecycle state matches it.
            .filter(({ lifecycle }) =>
              !campaignStateFilter ||
              String(lifecycle?.lifecycleState || "") === campaignStateFilter)
            .map(({ contact, lifecycle }, idx) => {
              const state = displayState(lifecycle);
              const sty = state
                ? (SES_STATE_STYLE[state] || LIFECYCLE_STATE_STYLE[state] || NEUTRAL_STATE_STYLE)
                : null;
              const isDryRunSent =
                state === "sent" && lifecycle?.dryRun === true;
              return (
                <Box key={contact?.id || `campaign-contact-${idx}`} sx={{ display: "flex", flexDirection: "column" }}>
                  {sty ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        pl: 0.75,
                        pr: 0.75,
                        pb: 0.25,
                      }}
                    >
                      <Typography sx={{ fontSize: 9, color: "#94A3B8", fontWeight: 600, letterSpacing: 0.5, textTransform: "uppercase" }}>
                        Campaign state
                      </Typography>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          px: 0.75,
                          py: 0.125,
                          borderRadius: "999px",
                          height: 16,
                          fontSize: 10,
                          fontWeight: 700,
                          textTransform: "capitalize",
                          color: sty.color,
                          backgroundColor: sty.bg,
                          border: `1px solid ${sty.border}`,
                        }}
                      >
                        {state}
                      </Box>
                      {isDryRunSent ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            px: 0.75,
                            py: 0.125,
                            borderRadius: "999px",
                            height: 16,
                            fontSize: 10,
                            fontWeight: 700,
                            color: "#3949AB",
                            backgroundColor: "#E8EAF6",
                            border: "1px solid #536DFE",
                          }}
                        >
                          dry run
                        </Box>
                      ) : null}
                    </Box>
                  ) : null}
                  <ContactCard contact={toContactCard(contact)} selected={false} />
                </Box>
              );
            })}
          {rows.length === 0 && (
            <Typography sx={{ fontSize: 12, color: "#64748B", textAlign: "center", py: 2.5 }}>
              No contacts match this campaign filter.
            </Typography>
          )}
        </Box>
      )}
    </AddressbookPanelShell>
  );
};

export default CampaignContactsPanel;
